import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mutator } from "@/shared/api";
import { claimError } from "@/shared/lib/errors/errorClaim";
import { handleSuccess } from "@/shared/lib/success";
import { supportFaqKeys } from "./queryKeys";
import type {
  CreateSupportFaqEntryPayload,
  CreateSupportFaqEntryResponse,
  DeleteSupportFaqEntryResponse,
  UpdateSupportFaqEntryPayload,
  UpdateSupportFaqEntryResponse,
} from "../model/types";

/**
 * `POST /support-faq/:id/view` — fire-and-forget view tracking. The backend
 * treats this as a metric, never a request the merchant is waiting on, so a
 * failure here must never surface: claim the error the same way
 * `useMutationErrorHandler` does, so the global fallback toast in
 * `queryClient.ts` stays quiet for a page-view side effect nobody asked to
 * see feedback about.
 */
export function useRecordFaqView() {
  return useMutation<void, unknown, string>({
    mutationFn: (faqId) => mutator<void>("POST", `/support-faq/${faqId}/view`),
    onError: (error) => claimError(error),
  });
}

/**
 * Ops authoring — `POST /system/support-faq`. The admin create drawer has no
 * draft workflow, so every call from that UI sends `isPublished: true`
 * explicitly; the backend still defaults to `false` for any other caller.
 */
export function useCreateSystemSupportFaqEntry() {
  const qc = useQueryClient();
  return useMutation<
    CreateSupportFaqEntryResponse,
    unknown,
    CreateSupportFaqEntryPayload
  >({
    mutationFn: (payload) =>
      mutator<CreateSupportFaqEntryResponse>("POST", "/support-faq", payload),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: "success.supportFaq.faqCreatedSuccessfully" });
      qc.invalidateQueries({ queryKey: supportFaqKeys.systemLists() });
    },
  });
}

/** Ops authoring — `PATCH /system/support-faq/:id`. Powers the edit flow on the same drawer used to create. */
export function useUpdateSystemSupportFaqEntry(id: string) {
  const qc = useQueryClient();
  return useMutation<
    UpdateSupportFaqEntryResponse,
    unknown,
    UpdateSupportFaqEntryPayload
  >({
    mutationFn: (payload) =>
      mutator<UpdateSupportFaqEntryResponse>("PATCH", `/support-faq/${id}`, payload),
    onSuccess: (data) => {
      handleSuccess(data, { title: "FAQ updated successfully" });
      qc.invalidateQueries({ queryKey: supportFaqKeys.systemLists() });
    },
  });
}

/** Ops authoring — `DELETE /system/support-faq/:id`. Hard delete, no soft-delete column. */
export function useDeleteSystemSupportFaqEntry() {
  const qc = useQueryClient();
  return useMutation<DeleteSupportFaqEntryResponse, unknown, string>({
    mutationFn: (id) =>
      mutator<DeleteSupportFaqEntryResponse>("DELETE", `/support-faq/${id}`),
    onSuccess: (data) => {
      handleSuccess(data, { titleKey: "success.supportFaq.faqDeletedSuccessfully" });
      qc.invalidateQueries({ queryKey: supportFaqKeys.systemLists() });
    },
  });
}
